import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Drawer,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Autocomplete,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from '../config/dayjs';
// Usando dayjs para formateo de fechas
import { useCheque, useNotification, useAuth } from '../context';
import * as catalogoService from '../services/catalogoService';
import * as chequeService from '../services/chequeService';
import { ConfirmDialog } from '../components/common';

// Mapeo de estados a colores
const ESTADO_COLORS = {
  pendiente: 'warning',
  depositado: 'info',
  con_novedad: 'warning',
  reprogramado: 'info',
  rechazado: 'error',
};

// Mapeo de estados a etiquetas
const ESTADO_LABELS = {
  pendiente: 'Pendiente',
  depositado: 'Depositado',
  con_novedad: 'Con Novedad',
  reprogramado: 'Reprogramado',
  rechazado: 'Rechazado',
};

const CheckListPage = () => {
  const navigate = useNavigate();
  const { cheques, loading, fetchCheques } = useCheque();
  const { showSuccess, showError } = useNotification();
  const { user } = useAuth();

  // Estado de paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Estado del sidebar de filtros
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  // Estado de diálogo de eliminación
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Estado de catálogos
  const [bancos, setBancos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loadingCatalogos, setLoadingCatalogos] = useState(true);

  // Estado de filtros
  const [filters, setFilters] = useState({
    estados: [],
    clienteId: null,
    bancoId: '',
    cajeroId: '',
    fechaPagoDesde: null,
    fechaPagoHasta: null,
    searchNumero: '',
  });

  // Cargar catálogos
  useEffect(() => {
    const loadCatalogos = async () => {
      try {
        setLoadingCatalogos(true);

        // Los cajeros no pueden ver usuarios, así que solo cargamos bancos y clientes
        const requests = [
          catalogoService.getBancos({ activo: true }),
          catalogoService.getClientes({ activo: true }),
        ];

        // Solo cargar usuarios si NO es cajero
        if (user?.rol !== 'cajero') {
          requests.push(catalogoService.getUsuarios({ activo: true }));
        }

        const results = await Promise.all(requests);
        setBancos(results[0]);
        setClientes(results[1]);

        // Solo asignar usuarios si se cargaron
        if (user?.rol !== 'cajero' && results[2]) {
          setUsuarios(results[2]);
        }
      } catch (error) {
        console.error('Error cargando catálogos:', error);
      } finally {
        setLoadingCatalogos(false);
      }
    };

    loadCatalogos();
  }, [user?.rol]);

  // Cargar cheques al montar
  useEffect(() => {
    fetchCheques();
  }, [fetchCheques]);

  // Filtrar cheques localmente
  const filteredCheques = useMemo(() => {
    let result = [...cheques];

    // Filtro por búsqueda de número
    if (filters.searchNumero) {
      const search = filters.searchNumero.toLowerCase();
      result = result.filter(cheque =>
        cheque.numero_cheque?.toLowerCase().includes(search)
      );
    }

    // Filtro por estados
    if (filters.estados.length > 0) {
      result = result.filter(cheque =>
        filters.estados.includes(cheque.estado)
      );
    }

    // Filtro por cliente
    if (filters.clienteId) {
      result = result.filter(cheque =>
        cheque.cliente_id === filters.clienteId ||
        cheque.cliente?.id === filters.clienteId
      );
    }

    // Filtro por banco
    if (filters.bancoId) {
      result = result.filter(cheque =>
        cheque.banco_id === filters.bancoId ||
        cheque.banco?.id === filters.bancoId
      );
    }

    // Filtro por cajero
    if (filters.cajeroId) {
      result = result.filter(cheque =>
        cheque.cajero_id === filters.cajeroId ||
        cheque.cajero?.id === filters.cajeroId
      );
    }

    // Filtro por rango de fecha de pago
    if (filters.fechaPagoDesde) {
      result = result.filter(cheque => {
        const fechaPago = new Date(cheque.fecha_pago);
        const fechaDesde = new Date(filters.fechaPagoDesde);
        fechaDesde.setHours(0, 0, 0, 0);
        return fechaPago >= fechaDesde;
      });
    }

    if (filters.fechaPagoHasta) {
      result = result.filter(cheque => {
        const fechaPago = new Date(cheque.fecha_pago);
        const fechaHasta = new Date(filters.fechaPagoHasta);
        fechaHasta.setHours(23, 59, 59, 999);
        return fechaPago <= fechaHasta;
      });
    }

    return result;
  }, [cheques, filters]);

  // Paginación
  const paginatedCheques = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredCheques.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredCheques, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRowClick = (chequeId) => {
    navigate(`/cheques/${chequeId}`);
  };

  const handleNuevoCheque = () => {
    navigate('/cheques/nuevo');
  };

  const handleEdit = (event, cheque) => {
    event.stopPropagation(); // Evitar que se active el click de la fila
    navigate(`/cheques/editar/${cheque.id}`);
  };

  const handleDelete = (event, cheque) => {
    event.stopPropagation(); // Evitar que se active el click de la fila
    setDeleteConfirm(cheque);
  };

  const confirmDelete = async () => {
    try {
      await chequeService.deleteCheque(deleteConfirm.id);
      showSuccess('Cheque eliminado correctamente');
      setDeleteConfirm(null);
      fetchCheques(); // Recargar la lista
    } catch (error) {
      console.error('Error al eliminar cheque:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Error al eliminar cheque';
      showError(errorMessage);
    }
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value,
    }));
    setPage(0); // Reset a primera página al filtrar
  };

  const handleClearFilters = () => {
    setFilters({
      estados: [],
      clienteId: null,
      bancoId: '',
      cajeroId: '',
      fechaPagoDesde: null,
      fechaPagoHasta: null,
      searchNumero: '',
    });
    setPage(0);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
    }).format(amount);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    try {
      return dayjs(date).format('DD/MM/YYYY');
    } catch {
      return '-';
    }
  };

  const getClienteName = (cheque) => {
    if (typeof cheque.cliente === 'object' && cheque.cliente?.nombre) {
      return cheque.cliente.nombre;
    }
    return '-';
  };

  const getBancoNombre = (cheque) => {
    if (typeof cheque.banco === 'object' && cheque.banco?.nombre) {
      return cheque.banco.nombre;
    }
    return '-';
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4">
            Gestión de Cheques
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={() => setFilterDrawerOpen(true)}
            >
              Filtros
            </Button>
            {['administrador', 'cajero', 'contador'].includes(user?.rol) && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleNuevoCheque}
              >
                Nuevo Cheque
              </Button>
            )}
          </Box>
        </Box>

        {/* Búsqueda rápida */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <TextField
              fullWidth
              placeholder="Buscar por número de cheque..."
              value={filters.searchNumero}
              onChange={(e) => handleFilterChange('searchNumero', e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </CardContent>
        </Card>

        {/* Tabla de cheques */}
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Número de Cheque</TableCell>
                  <TableCell>Cliente</TableCell>
                  <TableCell>Banco</TableCell>
                  <TableCell align="right">Monto</TableCell>
                  <TableCell>Fecha de Pago</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell align="center">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow key="loading-row">
                    <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : paginatedCheques.length === 0 ? (
                  <TableRow key="empty-row">
                    <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                      <Typography color="text.secondary">
                        No se encontraron cheques
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedCheques.map((cheque, index) => (
                    <TableRow
                      key={cheque.id}
                      hover
                      onClick={() => handleRowClick(cheque.id)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {cheque.numero_cheque}
                        </Typography>
                      </TableCell>
                      <TableCell>{getClienteName(cheque)}</TableCell>
                      <TableCell>{getBancoNombre(cheque)}</TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="medium">
                          {formatCurrency(cheque.monto)}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatDate(cheque.fecha_pago)}</TableCell>
                      <TableCell>
                        <Chip
                          label={ESTADO_LABELS[cheque.estado] || cheque.estado}
                          color={ESTADO_COLORS[cheque.estado] || 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          {cheque.estado === 'pendiente' && ['administrador', 'contador'].includes(user?.rol) && (
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={(e) => handleEdit(e, cheque)}
                              title="Editar cheque"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          )}
                          {user?.rol === 'administrador' && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(e) => handleDelete(e, cheque)}
                              title="Eliminar cheque"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredCheques.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Filas por página:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
            }
          />
        </Card>

        {/* Drawer de filtros */}
        <Drawer
          anchor="right"
          open={filterDrawerOpen}
          onClose={() => setFilterDrawerOpen(false)}
        >
          <Box sx={{ width: 350, p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Filtros
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={2}>
              {/* Estado */}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Estado</InputLabel>
                  <Select
                    multiple
                    value={filters.estados}
                    onChange={(e) => handleFilterChange('estados', e.target.value)}
                    label="Estado"
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip
                            key={value}
                            label={ESTADO_LABELS[value] || value}
                            size="small"
                          />
                        ))}
                      </Box>
                    )}
                  >
                    {Object.keys(ESTADO_LABELS).map((estado) => (
                      <MenuItem key={estado} value={estado}>
                        {ESTADO_LABELS[estado]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Cliente */}
              <Grid item xs={12}>
                <Autocomplete
                  options={clientes}
                  getOptionLabel={(option) => option.nombre || ''}
                  value={clientes.find(c => c.id === filters.clienteId) || null}
                  onChange={(_, newValue) => handleFilterChange('clienteId', newValue?.id || null)}
                  loading={loadingCatalogos}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Cliente"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingCatalogos ? <CircularProgress size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Banco */}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Banco</InputLabel>
                  <Select
                    value={filters.bancoId}
                    onChange={(e) => handleFilterChange('bancoId', e.target.value)}
                    label="Banco"
                  >
                    <MenuItem value="">
                      <em>Todos</em>
                    </MenuItem>
                    {bancos.map((banco) => (
                      <MenuItem key={banco.id} value={banco.id}>
                        {banco.nombre}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Cajero - Solo visible para no cajeros */}
              {user?.rol !== 'cajero' && (
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Cajero</InputLabel>
                    <Select
                      value={filters.cajeroId}
                      onChange={(e) => handleFilterChange('cajeroId', e.target.value)}
                      label="Cajero"
                    >
                      <MenuItem value="">
                        <em>Todos</em>
                      </MenuItem>
                      {usuarios.map((usuario) => (
                        <MenuItem key={usuario.id} value={usuario.id}>
                          {usuario.nombre}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {/* Fecha de pago desde */}
              <Grid item xs={12}>
                <DatePicker
                  label="Fecha de Pago Desde"
                  value={filters.fechaPagoDesde}
                  onChange={(date) => handleFilterChange('fechaPagoDesde', date)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                    },
                  }}
                />
              </Grid>

              {/* Fecha de pago hasta */}
              <Grid item xs={12}>
                <DatePicker
                  label="Fecha de Pago Hasta"
                  value={filters.fechaPagoHasta}
                  onChange={(date) => handleFilterChange('fechaPagoHasta', date)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                    },
                  }}
                />
              </Grid>

              {/* Botones */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleClearFilters}
                  >
                    Limpiar
                  </Button>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => setFilterDrawerOpen(false)}
                  >
                    Aplicar
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Drawer>

        {/* Diálogo de confirmación de eliminación */}
        <ConfirmDialog
          open={!!deleteConfirm}
          title="Eliminar Cheque"
          message={`¿Está seguro que desea eliminar el cheque ${deleteConfirm?.numero_cheque}? Esta acción no se puede deshacer.`}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteConfirm(null)}
          confirmText="Eliminar"
          cancelText="Cancelar"
        />
      </Box>
    </LocalizationProvider>
  );
};

export default CheckListPage;
